import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import {
  containsHtmlMarkup,
  normalizeAgreementContent,
} from "../utils/normalizeAgreementContent";

const agreementMarkdownComponents: Components = {
  h1: ({ children }) => (
    <h2 className="mb-4 mt-8 border-b border-gray-100 pb-2 text-lg font-semibold text-gray-900 first:mt-0">
      {children}
    </h2>
  ),
  h2: ({ children }) => (
    <h3 className="mb-3 mt-6 text-base font-semibold text-gray-900 first:mt-0">
      {children}
    </h3>
  ),
  h3: ({ children }) => (
    <h4 className="mb-2 mt-5 text-sm font-semibold text-gray-900 first:mt-0">
      {children}
    </h4>
  ),
  h4: ({ children }) => (
    <h5 className="mb-2 mt-4 text-sm font-medium text-gray-800 first:mt-0">
      {children}
    </h5>
  ),
  p: ({ children }) => (
    <p className="mb-4 text-[0.9375rem] leading-7 text-gray-700">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-4 list-disc space-y-2 pl-6 text-[0.9375rem] leading-7 text-gray-700">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-4 list-decimal space-y-2 pl-6 text-[0.9375rem] leading-7 text-gray-700">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-4 rounded-r-lg border-l-4 border-blue-300 bg-blue-50 px-4 py-3 text-[0.9375rem] leading-7 text-gray-700">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="mb-6 overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full border-collapse text-left text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gray-50 text-gray-900">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-gray-200 px-3 py-2 font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-200 px-3 py-2 text-gray-700">
      {children}
    </td>
  ),
  code: ({ className, children }) => {
    const isBlock = Boolean(className);
    if (isBlock) {
      return (
        <pre className="mb-4 overflow-x-auto rounded-lg bg-gray-100 p-4 text-xs text-gray-800">
          <code className={className}>{children}</code>
        </pre>
      );
    }
    return (
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-800">
        {children}
      </code>
    );
  },
  hr: () => <hr className="my-8 border-gray-200" />,
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
};

interface AgreementMarkdownProps {
  content: string;
}

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a ?? []), "target", "rel"],
    div: [...(defaultSchema.attributes?.div ?? []), "className"],
    span: [...(defaultSchema.attributes?.span ?? []), "className"],
    p: [...(defaultSchema.attributes?.p ?? []), "className"],
  },
};

const AgreementMarkdown = ({ content }: AgreementMarkdownProps) => {
  const { t } = useTranslation("auth");
  const normalized = normalizeAgreementContent(content);
  const useHtmlPipeline = containsHtmlMarkup(normalized);

  if (!normalized) {
    return (
      <p className="text-sm italic text-gray-500">{t("agreement.noAgreementText")}</p>
    );
  }

  return (
    <div className="agreement-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={
          useHtmlPipeline
            ? [rehypeRaw, [rehypeSanitize, sanitizeSchema]]
            : undefined
        }
        components={agreementMarkdownComponents}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
};

export default AgreementMarkdown;
